import { Divider, Group } from '@byecode/ui'
import type { StripePayConfig } from '@lighthouse/core'
import type { StripeActionConfig } from '@lighthouse/shared'
import { type FlowNode, ListItem4ByecodeUi, OUTPUT_SINGLE_DATA_NODES, OUTPUT_SINGLE_RECORD_NODES, useAtomData } from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'

import { ActionAuthIntegrateCard } from '../../Common/ActionAuthIntegrateCard'
import { UnitNoAuth } from '../../Common/UnitNoAuth'
import * as CM from '../../styles'
import { SelectRecordControlled } from '../component/SelectRecordControlled'
import Pay from './Pay'

interface StripeActionConfigureProps {
    nodeType?: string
    allParentNodes?: FlowNode[]
    allParentActions?: import('@lighthouse/core').IndependentAction[]
    actionTypeSwitcher?: React.ReactNode
    value: StripeActionConfig
    onChange: (value: StripeActionConfig) => void
}

export const StripeActionConfigure: React.FunctionComponent<StripeActionConfigureProps> = ({
    actionTypeSwitcher,
    allParentNodes,
    allParentActions,
    value,
    onChange
}) => {
    const { type } = useFlowContainerContext()
    const isWorkflow = !!type
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const upstreamNodesForVariableSelect = useMemo(
        () => allParentNodes?.filter(item => OUTPUT_SINGLE_DATA_NODES.has(item.data.nodeType)),
        [allParentNodes]
    )
    const upstreamNodesForSource = useMemo(
        () => allParentNodes?.filter(item => OUTPUT_SINGLE_RECORD_NODES.has(item.data.nodeType)),
        [allParentNodes]
    )

    const stripeAccount = useMemo(() => otherPlatformList?.find(item => item.type === 'STRIPE'), [otherPlatformList])

    const header = useMemo(() => {
        if (!actionTypeSwitcher) {
            return null
        }
        return (
            <>
                <Group label="动作配置">
                    <ListItem4ByecodeUi>
                        <CM.FormItemLabelWrapper>
                            <CM.FormItemLabel>类型</CM.FormItemLabel>
                        </CM.FormItemLabelWrapper>
                        <CM.FormItemContent>{actionTypeSwitcher}</CM.FormItemContent>
                    </ListItem4ByecodeUi>
                </Group>
                <Divider />
            </>
        )
    }, [actionTypeSwitcher])

    if (!stripeAccount) {
        return (
            <>
                {header}
                <UnitNoAuth type="STRIPE" />
            </>
        )
    }

    return (
        <CM.Container>
            {header}
            <ActionAuthIntegrateCard type="STRIPE" />
            <Divider />
            <Group label="选择支付订单">
                <SelectRecordControlled
                    value={value}
                    onChange={next => {
                        onChange(next as StripePayConfig)
                    }}
                    upstreamNodesForVariableSelect={upstreamNodesForVariableSelect}
                    upstreamNodesForSource={upstreamNodesForSource}
                    allParentActions={allParentActions}
                    upstreamRecordIsFilterAndSort={false}
                    disableUpstreamRecord={!isWorkflow}
                />
            </Group>
            <Divider color="var(--color-gray-200)" />
            <Pay allParentNodes={allParentNodes} value={value} onChange={onChange} />
        </CM.Container>
    )
}
